import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { toastForLiveEvent } from '../lib/liveToasts';
import { logger } from '../lib/logger';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [dashboardUpdates, setDashboardUpdates] = useState(null);
  const [lastQueueEvent, setLastQueueEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Get WebSocket URL from backend URL
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = backendUrl.replace(/^https?/, wsProtocol);
    // Auth is carried by the httpOnly session cookie on this same-origin handshake
    return `${baseUrl}/api/ws`;
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'connection_established':
        logger.debug('WebSocket connection confirmed');
        break;
      case 'pong':
        break;
      case 'notification':
        setNotifications(prev => [data.data, ...prev].slice(0, 50));
        toast.info(data.data.title, { description: data.data.message });
        break;
      case 'dashboard_update':
        setDashboardUpdates(data.data);
        break;
      case 'queue_update':
        setLastQueueEvent({ ...data, seq: Date.now() });
        toastForLiveEvent(data, navigate, pathRef.current);
        break;
      case 'patient_event':
      case 'billing_event':
      case 'appointment_event':
        toastForLiveEvent(data, navigate, pathRef.current);
        break;
      default:
        logger.debug('Unhandled WebSocket message type:', data.type);
    }
  }, [navigate]);

  // Disconnect function (defined before connect since connect uses it indirectly)
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      if (ws.readyState === WebSocket.CONNECTING) {
        // Closing mid-handshake logs a browser warning; let it open, then close cleanly
        ws.onopen = () => ws.close(1000, 'Superseded');
        ws.onmessage = null;
        ws.onclose = null;
      } else {
        ws.close(1000, 'User logout');
      }
      wsRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Connect to WebSocket - uses refs to avoid closure issues
  useEffect(() => {
    if (!user) {
      disconnect();
      return;
    }

    const attemptConnect = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN || 
          wsRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      try {
        const wsUrl = getWsUrl();
        logger.debug('Connecting to WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          logger.debug('WebSocket connected');
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
          
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setLastMessage(data);
            handleMessage(data);
          } catch (e) {
            logger.error('Failed to parse WebSocket message:', e);
          }
        };

        ws.onclose = (event) => {
          logger.debug('WebSocket disconnected:', event.code, event.reason);
          setIsConnected(false);
          wsRef.current = null;
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          // Reconnect logic using refs to avoid stale closure
          if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            logger.debug(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              attemptConnect();
            }, delay);
          }
        };

        ws.onerror = (error) => {
          logger.error('WebSocket error:', error);
        };

      } catch (error) {
        logger.error('Failed to create WebSocket:', error);
      }
    };

    attemptConnect();

    return () => {
      disconnect();
    };
  }, [user, getWsUrl, handleMessage, disconnect]);

  // Manual connect function for external use
  const connect = useCallback(() => {
    if (!user) return;
    disconnect();
    // The useEffect will handle reconnection
  }, [user, disconnect]);

  // Send message helper
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    isConnected,
    lastMessage,
    notifications,
    dashboardUpdates,
    lastQueueEvent,
    sendMessage,
    clearNotifications,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
